import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "@/lib/is-tauri";
import {
  RECORD_SCREEN_BINDING,
  findClaudeShortcutClashes,
  type ClaudeAppSetting,
  type ClaudeAppShortcut,
  type ClaudeShortcutClash,
  type ClaudeTurnOff,
  type ClashTarget,
} from "@/lib/claude-app-shortcuts";
import {
  SHORTCUT_REGISTRY,
  getDefaultBinding,
  type ShortcutBinding,
} from "@/lib/shortcut-registry";
import { useShortcutStore } from "./shortcut-store";

type State = {
  /** The Claude app's active global shortcuts, as last read. */
  shortcuts: ClaudeAppShortcut[];
  /** Read Claude's settings file again (desktop only). */
  refresh: () => Promise<void>;
  /**
   * Turn a Claude shortcut off in one profile's settings file (`profile`:
   * its folder, `null` for the default), then read again. `automatic` is
   * Denker acting on its own: once per profile and setting ever, and only
   * while no Claude of that profile runs. Otherwise that profile's running
   * Claude is asked to quit first and opened again after. Throws when the
   * file cannot be changed.
   */
  turnOff: (
    setting: ClaudeAppSetting,
    profile: string | null,
    automatic: boolean,
  ) => Promise<ClaudeTurnOff>;
};

let reads = 0;
let latest: Promise<void> = Promise.resolve();

export const useClaudeAppShortcutStore = create<State>((set, get) => ({
  shortcuts: [],
  /** Settles once the newest read is in, so callers never see an older one. */
  refresh: async () => {
    if (!isTauri()) return;
    const id = ++reads;
    const read = (async () => {
      try {
        const shortcuts = await invoke<ClaudeAppShortcut[]>(
          "get_claude_app_shortcuts",
        );
        // An older read answering late must not undo a newer one; an answer
        // that is not a list reads as no Claude shortcuts.
        if (id === reads)
          set({ shortcuts: Array.isArray(shortcuts) ? shortcuts : [] });
      } catch {
        // Keep the last reading: the notice is advice, never a blocker.
      }
    })();
    latest = read;
    let awaited: Promise<void>;
    do {
      awaited = latest;
      await awaited;
    } while (awaited !== latest);
  },
  turnOff: async (setting, profile, automatic) => {
    const result = await invoke<ClaudeTurnOff>("turn_off_claude_app_shortcut", {
      setting,
      profile,
      automatic,
    });
    if (result === "turnedOff" || result === "restarted") await get().refresh();
    return result;
  },
}));

type Saved = Pick<
  ReturnType<typeof useShortcutStore.getState>,
  "overrides" | "disabled"
>;

/** Claude's shortcuts against Denker's active bindings and Caps Lock recording. */
export function claudeShortcutClashes(
  claude: ClaudeAppShortcut[],
  { overrides, disabled }: Saved,
): ClaudeShortcutClash[] {
  if (!claude.length) return [];
  const denker: Array<{ target: ClashTarget; binding: ShortcutBinding }> =
    SHORTCUT_REGISTRY.filter(({ id }) => !disabled.includes(id)).map(
      ({ id }) => ({
        target: id,
        binding: overrides[id] ?? getDefaultBinding(id),
      }),
    );
  denker.push({ target: "record-screen", binding: RECORD_SCREEN_BINDING });
  return findClaudeShortcutClashes(claude, denker);
}

/** Read Claude's file now and whenever this window regains focus. */
export function useClaudeAppShortcutRefresh(): void {
  const refresh = useClaudeAppShortcutStore((state) => state.refresh);
  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);
}

/** The live clashes; they go as soon as Claude's file or Denker's keys change. */
export function useClaudeShortcutClashes(): ClaudeShortcutClash[] {
  const claude = useClaudeAppShortcutStore((state) => state.shortcuts);
  const overrides = useShortcutStore((state) => state.overrides);
  const disabled = useShortcutStore((state) => state.disabled);
  return useMemo(
    () => claudeShortcutClashes(claude, { overrides, disabled }),
    [claude, overrides, disabled],
  );
}
